import React, { useState, useMemo, useRef } from 'react';
import {
    Box, Grid, TextField, MenuItem, Typography
} from '@mui/material';
import {
    Badge as RegFormIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader from '../components/PrintAgencyHeader';
import { academyStore } from '../services/academyStore';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { exportToExcel, exportToWord } from '../utils/printExportUtils';

export const PrintRegistrationFormPage: React.FC = () => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const isRtl = language === 'ar';

    const students = academyStore.getStudents();
    const groups = academyStore.getGroups();
    const groupStudents = academyStore.getGroupStudents();

    const [selectedStudentId, setSelectedStudentId] = useState<number | 'blank'>('blank');
    const printableRef = useRef<HTMLDivElement>(null);

    const selectedStudent = useMemo(() => {
        if (selectedStudentId === 'blank') return null;
        return students.find(s => s.id === selectedStudentId) || null;
    }, [students, selectedStudentId]);

    const studentGroup = useMemo(() => {
        if (!selectedStudent) return '';
        const gs = groupStudents.find(g => g.student_id === selectedStudent.id && g.status !== 'Dropped');
        if (gs) {
            const grp = groups.find(g => g.id === gs.group_id);
            if (grp) return grp.name;
        }
        return selectedStudent.school_level || '';
    }, [selectedStudent, groupStudents, groups]);

    const currentYear = new Date().getFullYear();
    const academicYear = `${currentYear}-${currentYear + 1}`;

    const studentFullName = selectedStudent ? `${selectedStudent.last_name} ${selectedStudent.first_name}`.trim() : '—';
    const dob = selectedStudent?.date_of_birth || '—';
    const classLabel = studentGroup || '—';
    const parentName = selectedStudent ? (selectedStudent.guardian_first_name ? `${selectedStudent.guardian_first_name} ${selectedStudent.guardian_last_name || ''}`.trim() : '—') : '—';
    const parentPhone = selectedStudent?.guardian_phone || selectedStudent?.phone || '—';
    const profession = selectedStudent?.profession || '—';
    const address = selectedStudent?.address || '—';
    const emergency = selectedStudent?.emergency_contact || '—';

    // Export Handlers
    const handleExportExcel = () => {
        const headers = [
            language === 'ar' ? 'البيان' : language === 'en' ? 'Field' : 'Champ',
            language === 'ar' ? 'القيمة' : language === 'en' ? 'Value' : 'Valeur'
        ];
        const rows = [
            [language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'Nom et prénom', studentFullName],
            [language === 'ar' ? 'تاريخ الميلاد' : language === 'en' ? 'Date of birth' : 'Date de naissance', dob],
            [language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe', classLabel],
            [language === 'ar' ? 'الولي' : language === 'en' ? 'Parent / Guardian' : 'Parent (nom)', parentName],
            [language === 'ar' ? 'هاتف الولي' : language === 'en' ? 'Parent Phone' : 'Téléphone parent', parentPhone],
            [language === 'ar' ? 'المهنة' : language === 'en' ? 'Profession' : 'Profession', profession],
            [language === 'ar' ? 'العنوان' : language === 'en' ? 'Address' : 'Adresse', address],
            [language === 'ar' ? 'في حالة الطوارئ' : language === 'en' ? 'Emergency' : "En cas d'urgence", emergency]
        ];
        exportToExcel(`Fiche_Inscription_${selectedStudent?.code || 'Vierge'}`, 'Inscription', headers, rows);
    };

    const handleExportWord = () => {
        if (!printableRef.current) return;
        const title = language === 'ar' ? 'بطاقة تسجيل التلميذ' : language === 'en' ? 'Student Registration Form' : "Fiche d'inscription";
        exportToWord(`Fiche_Inscription_${selectedStudent?.code || 'Vierge'}`, title, printableRef.current.innerHTML, isRtl);
    };

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties => ({
        border: '1px solid #777',
        padding: '6px 8px',
        textAlign: isRtl ? 'right' : 'left',
        width,
        ...extra
    });

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'اختيار' : language === 'en' ? 'Select' : 'Choisir'}
                    value={selectedStudentId}
                    onChange={e => setSelectedStudentId(e.target.value === 'blank' ? 'blank' : Number(e.target.value))}
                >
                    <MenuItem value="blank">
                        {language === 'ar' ? '— استمارة فارغة —' : language === 'en' ? '— Blank Form —' : '— Fiche vierge —'}
                    </MenuItem>
                    {students.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.last_name} {s.first_name} {s.code ? `(${s.code})` : ''}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
        </Grid>
    );

    return (
        <GenericPage
            title={language === 'ar' ? 'بطاقة تسجيل التلميذ' : language === 'en' ? 'Registration Form' : "Fiche d'inscription de l'apprenant"}
            icon={<RegFormIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
            onExportExcel={handleExportExcel}
            onExportWord={handleExportWord}
        >
            <div ref={printableRef} dir={isRtl ? 'rtl' : 'ltr'} style={{ maxWidth: 820, margin: '0 auto', color: '#111', fontFamily: 'Arial, sans-serif' }}>
                <PrintAgencyHeader
                    title={language === 'ar' ? 'بطاقة تسجيل التلميذ' : language === 'en' ? 'STUDENT REGISTRATION FORM' : "FICHE D'INSCRIPTION"}
                    subtitle={`${language === 'ar' ? 'السنة الدراسية' : language === 'en' ? 'Academic Year' : 'Année scolaire'} ${academicYear}`}
                    showBorderBottom
                />

                <Box sx={{ my: 2 }}>
                    {/* Top Identity Section with Photo Frame */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
                        <table style={{ flex: 1, borderCollapse: 'collapse', fontSize: 12 }}>
                            <tbody>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'Nom et prénom'}
                                    </td>
                                    <td style={cellStyle(undefined, { fontWeight: 600 })}>{studentFullName}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'تاريخ الميلاد' : language === 'en' ? 'Date of birth' : 'Date de naissance'}
                                    </td>
                                    <td style={cellStyle()}>{dob}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe'}
                                    </td>
                                    <td style={cellStyle()}>{classLabel}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'الولي (الاسم)' : language === 'en' ? 'Parent (Name)' : 'Parent (nom)'}
                                    </td>
                                    <td style={cellStyle()}>{parentName}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'هاتف الولي' : language === 'en' ? 'Parent Phone' : 'Téléphone du parent'}
                                    </td>
                                    <td style={cellStyle()}>{parentPhone}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'المهنة' : language === 'en' ? 'Profession' : 'Profession'}
                                    </td>
                                    <td style={cellStyle()}>{profession}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'العنوان' : language === 'en' ? 'Address' : 'Adresse'}
                                    </td>
                                    <td style={cellStyle()}>{address}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(190, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'في حالة الطوارئ' : language === 'en' ? 'In emergency' : "En cas d'urgence"}
                                    </td>
                                    <td style={cellStyle()}>{emergency}</td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Photo Box */}
                        <div style={{
                            width: 120,
                            border: '1px dashed #777',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fafafa',
                            padding: 8,
                            flexShrink: 0
                        }}>
                            {selectedStudent?.photo_base64 ? (
                                <img
                                    src={selectedStudent.photo_base64}
                                    alt="Student"
                                    style={{ width: '100%', maxHeight: 150, objectFit: 'cover' }}
                                />
                            ) : (
                                <span style={{ fontSize: 11, color: '#888', textAlign: 'center' }}>
                                    {language === 'ar' ? 'صورة' : language === 'en' ? 'Photo' : 'Photo'}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Middle Two-Column Section: Scolarité & Pièces du dossier */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                        <tbody>
                            <tr>
                                {/* Left column: Scolarité */}
                                <td style={{ width: '50%', verticalAlign: 'top', border: 'none', paddingInlineEnd: 10 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f2f2f2' }}>
                                                <th colSpan={2} style={{ ...cellStyle(), fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                    {language === 'ar' ? 'الدراسة' : language === 'en' ? 'Tuition' : 'Scolarité'}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'تاريخ التسجيل' : language === 'en' ? 'Enrollment date' : "Date d'inscription"}</td>
                                                <td style={cellStyle()}>—</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'رسوم التسجيل' : language === 'en' ? 'Registration fees' : "Frais d'inscription"}</td>
                                                <td style={cellStyle()}>—</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'الاشتراك الشهري' : language === 'en' ? 'Monthly fee' : 'Mensualité'}</td>
                                                <td style={cellStyle()}>—</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'التخفيض' : language === 'en' ? 'Discount' : 'Remise'}</td>
                                                <td style={cellStyle()}>{selectedStudent?.discount_name ? `${selectedStudent.discount_name}` : '—'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>

                                {/* Right column: Pièces du dossier */}
                                <td style={{ width: '50%', verticalAlign: 'top', border: 'none', paddingInlineStart: 10 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f2f2f2' }}>
                                                <th style={{ ...cellStyle(), fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                    {language === 'ar' ? 'وثائق الملف' : language === 'en' ? 'Required Documents' : 'Pièces du dossier'}
                                                </th>
                                                <th style={{ ...cellStyle(35), textAlign: 'center' }}>✓</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={cellStyle()}>{language === 'ar' ? 'شهادة الميلاد' : language === 'en' ? 'Birth certificate' : 'Acte de naissance'}</td>
                                                <td style={{ ...cellStyle(35), textAlign: 'center' }}>□</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle()}>{language === 'ar' ? 'شهادة طبية' : language === 'en' ? 'Medical certificate' : 'Certificat médical'}</td>
                                                <td style={{ ...cellStyle(35), textAlign: 'center' }}>□</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle()}>{language === 'ar' ? 'صور شمسية' : language === 'en' ? 'Identity photos' : "Photos d'identité"}</td>
                                                <td style={{ ...cellStyle(35), textAlign: 'center' }}>□</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle()}>{language === 'ar' ? 'دفتر التلقيح' : language === 'en' ? 'Vaccination booklet' : 'Carnet de vaccination'}</td>
                                                <td style={{ ...cellStyle(35), textAlign: 'center' }}>□</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle()}>{language === 'ar' ? 'كشف نقاط السنة الماضية' : language === 'en' ? 'Previous report card' : "Bulletin de l'année précédente"}</td>
                                                <td style={{ ...cellStyle(35), textAlign: 'center' }}>□</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Commitment Paragraph */}
                    <div style={{
                        marginTop: 15,
                        padding: 10,
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 4,
                        fontSize: 12,
                        lineHeight: 1.6,
                        textAlign: 'justify'
                    }}>
                        {language === 'ar' ? (
                            'أصرح أنا الولي بصحة المعلومات المذكورة أعلاه وألتزم بتسديد مصاريف الدراسة وفق شروط ونظام المؤسسة.'
                        ) : language === 'en' ? (
                            'I, the undersigned parent or guardian, certify that the above information is correct and undertake to pay tuition fees according to institutional conditions.'
                        ) : (
                            "Je soussigné(e), parent ou tuteur, déclare exactes les informations ci-dessus et m'engage à régler la scolarité selon les conditions de l'établissement."
                        )}
                    </div>

                    {/* Signatures */}
                    <table style={{ width: '100%', marginTop: 35, borderCollapse: 'collapse', fontSize: 12 }}>
                        <tbody>
                            <tr>
                                <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'right' : 'left' }}>
                                    <strong>{language === 'ar' ? 'الولي :' : language === 'en' ? 'Parent / Guardian :' : 'Le parent :'}</strong>
                                    <div style={{ height: 60 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '70%' }} />
                                </td>
                                <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'left' : 'right' }}>
                                    <strong>{language === 'ar' ? 'المدير (الختم) :' : language === 'en' ? 'Director (Stamp) :' : 'Le directeur (cachet) :'}</strong>
                                    <div style={{ height: 60 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '70%', marginInlineStart: 'auto' }} />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ marginTop: 30, textAlign: 'center', fontSize: 11, color: '#666' }}>
                        {language === 'ar' ? 'شكراً على ثقتكم.' : language === 'en' ? 'Thank you for your trust.' : 'Merci de votre confiance.'}
                    </div>
                </Box>
            </div>
        </GenericPage>
    );
};

export default PrintRegistrationFormPage;
